import { Sparkles } from 'lucide-react';
import { useState } from 'react';

import { Modal } from '../../components/ui/Modal';
import { useAuth } from '../auth/auth-context';

import { changelogFor } from './changelog';

// Dernière nouveauté consultée, mémorisée dans ce navigateur (simple confort
// d'affichage, aucune donnée sensible).
const SEEN_KEY = 'adweb.changelogSeen';

function readSeen(): string | null {
  try {
    return window.localStorage.getItem(SEEN_KEY);
  } catch {
    return null;
  }
}

/** Bouton « Nouveautés » de la barre du haut, avec pastille tant qu'il y a du nouveau. */
export function WhatsNewButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState(readSeen);
  if (!user) return null;
  const entries = changelogFor(user.role);
  const latest = entries[0]?.id;
  if (!latest) return null;
  const hasNew = seen !== latest;

  function openPanel() {
    setOpen(true);
    setSeen(latest!);
    try {
      window.localStorage.setItem(SEEN_KEY, latest!);
    } catch {
      // Stockage indisponible : la pastille reviendra au prochain chargement.
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={openPanel}
        className="relative flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-sm text-slate-600 transition hover:bg-slate-100"
        aria-label={hasNew ? 'Nouveautés (non lues)' : 'Nouveautés'}
      >
        <Sparkles className="h-4 w-4" aria-hidden="true" />
        <span className="hidden sm:inline">Nouveautés</span>
        {hasNew && (
          <span
            className="absolute right-1 top-1 h-2 w-2 rounded-full bg-accent"
            aria-hidden="true"
          />
        )}
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Nouveautés" size="lg">
        <div className="space-y-6">
          {entries.map((e) => (
            <section key={e.id} aria-labelledby={`changelog-${e.id}`}>
              <div className="mb-2 flex items-baseline justify-between gap-3">
                <h3 id={`changelog-${e.id}`} className="text-sm font-bold text-slate-900">
                  {e.title}
                </h3>
                <time dateTime={e.date} className="shrink-0 text-xs text-slate-500">
                  {new Date(e.date).toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </time>
              </div>
              <ul className="space-y-2">
                {e.items.map((item) => (
                  <li key={item.text} className="flex gap-2 text-sm leading-relaxed text-slate-700">
                    <span
                      className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent"
                      aria-hidden="true"
                    />
                    <span>{item.text}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </Modal>
    </>
  );
}
